import { useEffect, useState, useCallback } from "react";
import { Trash2, Plus, Edit3, LogOut, Save, X, RefreshCcw, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, adminApi, getAdminToken, setAdminToken } from "@/lib/api";

const TABS = ["Bookings", "Subscribers", "Orders", "Blog", "Testimonials", "Programs"];

export default function Admin() {
  const [token, setToken] = useState(getAdminToken());

  if (!token) return <Login onLogin={(t) => { setAdminToken(t); setToken(t); }} />;
  return <Dashboard token={token} onLogout={() => { setAdminToken(""); setToken(""); }} />;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      onLogin(data.token);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-[2rem] p-10 border border-[#F3C9D3]/40"
        data-testid="admin-login-form"
      >
        <h1 className="font-serif-display text-3xl text-[#3A3A3A] mb-2">Admin</h1>
        <p className="text-sm text-[#5C5C5C] mb-6">
          Sign in to manage bookings, orders, blog & programs.
        </p>
        <input
          type="password"
          autoFocus
          placeholder="Admin password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          data-testid="admin-password-input"
          className="w-full bg-white border border-[#F3C9D3] rounded-xl px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-[#E8A6B8]"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="btn-pink w-full disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("Bookings");
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <h1 className="font-serif-display text-4xl text-[#3A3A3A]">Admin</h1>
        <button
          onClick={onLogout}
          className="text-sm inline-flex items-center gap-2 text-[#3A3A3A] hover:text-[#E8A6B8]"
          data-testid="admin-logout"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#F3C9D3]/40">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-semibold border-b-2 -mb-px transition ${
              tab === t
                ? "border-[#E8A6B8] text-[#3A3A3A]"
                : "border-transparent text-[#5C5C5C] hover:text-[#3A3A3A]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "Bookings" && <BookingsTab token={token} />}
      {tab === "Subscribers" && <SubscribersTab token={token} />}
      {tab === "Orders" && <OrdersTab token={token} />}
      {tab === "Blog" && <BlogTab token={token} />}
      {tab === "Testimonials" && <TestimonialsTab token={token} />}
      {tab === "Programs" && <ProgramsTab token={token} />}
    </div>
  );
}

const EMPTY_TESTIMONIAL = {
  quote: "",
  body: "",
  name: "",
  role: "",
  active: true,
  order: 0,
};

function TestimonialsTab({ token }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    adminApi(token).get("/admin/testimonials").then((r) => setItems(r.data));
  }, [token]);
  useEffect(load, [load]);

  const save = async (form) => {
    try {
      const payload = { ...form, order: parseInt(form.order, 10) || 0 };
      if (editing && editing.id) {
        await adminApi(token).put(`/admin/testimonials/${editing.id}`, payload);
        toast.success("Testimonial updated");
      } else {
        await adminApi(token).post("/admin/testimonials", payload);
        toast.success("Testimonial added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await adminApi(token).delete(`/admin/testimonials/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const toggleActive = async (t) => {
    try {
      await adminApi(token).put(`/admin/testimonials/${t.id}`, { active: !t.active });
      load();
    } catch {
      toast.error("Could not toggle");
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <ActionRow
        onRefresh={load}
        count={items.length}
        label="testimonials"
        action={
          <button
            onClick={() => setEditing({ ...EMPTY_TESTIMONIAL, order: items.length })}
            className="btn-pink text-xs"
            data-testid="admin-new-testimonial"
          >
            <Plus size={14} /> New Testimonial
          </button>
        }
      />
      {editing && (
        <TestimonialEditor
          initial={editing}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((t) => (
          <div
            key={t.id}
            className={`bg-white rounded-2xl border border-[#F3C9D3]/40 p-6 ${
              t.active ? "" : "opacity-60"
            }`}
            data-testid={`admin-testimonial-${t.id}`}
          >
            <p className="font-serif-display italic text-xl text-[#3A3A3A] leading-snug">
              "{t.quote}"
            </p>
            <p className="text-xs text-[#5C5C5C] mt-2 line-clamp-3">{t.body}</p>
            <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] mt-3">
              {t.name}
              {t.role && <span className="text-[#5C5C5C] font-normal normal-case tracking-normal"> · {t.role}</span>}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-[#5C5C5C]">Order: {t.order}</span>
              <button
                onClick={() => toggleActive(t)}
                className={`px-3 py-1.5 rounded-full ${t.active ? "bg-[#F3C9D3]/60" : "bg-[#F6EFE9]"}`}
                data-testid={`toggle-${t.id}`}
              >
                {t.active ? "Active" : "Hidden"}
              </button>
              <button
                onClick={() => setEditing(t)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F6EFE9] hover:bg-[#F3C9D3]"
                data-testid={`edit-testimonial-${t.id}`}
              >
                <Edit3 size={12} /> Edit
              </button>
              <button
                onClick={() => remove(t.id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-red-700 bg-red-50 hover:bg-red-100"
                data-testid={`delete-testimonial-${t.id}`}
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialEditor({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const update = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[#F3C9D3]/40 p-6 mb-6 space-y-4" data-testid="admin-testimonial-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-serif-display text-2xl text-[#3A3A3A]">{form.id ? "Edit Testimonial" : "New Testimonial"}</h3>
        <button type="button" onClick={onCancel} className="text-[#5C5C5C] hover:text-[#3A3A3A]"><X size={20} /></button>
      </div>
      <input className="hfh-admin-input" placeholder="Short quote (e.g. I feel strong again)" required value={form.quote} onChange={update("quote")} data-testid="t-quote" />
      <textarea className="hfh-admin-input" placeholder="Longer story / body text" rows={4} required value={form.body} onChange={update("body")} data-testid="t-body" />
      <div className="grid sm:grid-cols-2 gap-4">
        <input className="hfh-admin-input" placeholder="Name (e.g. Sarah)" required value={form.name} onChange={update("name")} data-testid="t-name" />
        <input className="hfh-admin-input" placeholder="Role (e.g. Online Coaching client)" value={form.role || ""} onChange={update("role")} data-testid="t-role" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 items-center">
        <input className="hfh-admin-input" type="number" placeholder="Display order (0 = first)" value={form.order} onChange={update("order")} />
        <label className="text-sm text-[#3A3A3A] inline-flex items-center gap-2">
          <input type="checkbox" checked={!!form.active} onChange={update("active")} /> Active on site
        </label>
      </div>
      <div className="flex gap-3">
        <button type="submit" className="btn-pink text-xs" data-testid="t-save"><Save size={14}/> Save</button>
        <button type="button" onClick={onCancel} className="btn-outline text-xs">Cancel</button>
      </div>
      <style>{`.hfh-admin-input{width:100%;background:#fff;border:1px solid #F3C9D3;border-radius:0.85rem;padding:0.7rem 1rem;font-size:0.9rem;color:#3A3A3A;font-family:inherit}
      .hfh-admin-input:focus{outline:none;border-color:#E8A6B8;box-shadow:0 0 0 4px rgba(232,166,184,0.15)}`}</style>
    </form>
  );
}

function SubscribersTab({ token }) {
  const [items, setItems] = useState([]);
  const [automation, setAutomation] = useState(null);

  const load = useCallback(() => {
    adminApi(token).get("/admin/subscribers").then((r) => setItems(r.data));
    adminApi(token)
      .get("/admin/automation/warm-followup")
      .then((r) => setAutomation(r.data))
      .catch(() => {});
  }, [token]);
  useEffect(load, [load]);

  const remove = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await adminApi(token).delete(`/admin/subscribers/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  const sendWarmFollowup = async (sub) => {
    const note = sub.warm_followup_sent_at
      ? "This subscriber already received the warm follow-up. Send again anyway?"
      : `Send the warm follow-up to ${sub.email} now?`;
    if (!window.confirm(note)) return;
    try {
      const { data } = await adminApi(token).post(
        `/admin/subscribers/${sub.id}/send-warm-followup`,
      );
      if (data.sent) {
        toast.success("Warm follow-up sent");
        load();
      } else {
        toast.error("Could not send (check Resend logs)");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    }
  };

  const exportCsv = () => {
    const header = "email,source,downloads,last_downloaded_at,warm_followup_sent_at,created_at\n";
    const rows = items
      .map(
        (s) =>
          `${s.email},${s.source},${s.download_count || 0},${s.last_downloaded_at || ""},${s.warm_followup_sent_at || ""},${s.created_at}`,
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadedCount = items.filter((s) => (s.download_count || 0) > 0).length;
  const totalDownloads = items.reduce((sum, s) => sum + (s.download_count || 0), 0);

  return (
    <div data-testid="admin-subscribers">
      <ActionRow
        onRefresh={load}
        count={items.length}
        label="subscribers"
        action={
          <button onClick={exportCsv} className="btn-outline text-xs" data-testid="export-subscribers">
            Export CSV
          </button>
        }
      />
      {items.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <StatTile label="Total subscribers" value={items.length} />
          <StatTile label="Downloaded guide" value={`${downloadedCount} / ${items.length}`} />
          <StatTile label="Total downloads" value={totalDownloads} />
        </div>
      )}

      {automation && (
        <div className="bg-white border border-[#F3C9D3]/40 rounded-2xl p-5 mb-5" data-testid="warm-followup-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <span className="inline-flex p-2.5 rounded-xl bg-[#F3C9D3]/40">
                <Mail size={18} className="text-[#E8A6B8]" strokeWidth={1.6} />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A]">
                  Warm follow-up automation
                </p>
                <p className="text-sm text-[#5C5C5C] mt-1">
                  Sends a personal check-in from Maya when a subscriber downloads the guide{" "}
                  <strong>≥ 2 times</strong>, <strong>24 hours</strong> after their last download.
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            <MiniStat label="Engaged" value={automation.engaged_subscribers} />
            <MiniStat label="Follow-ups sent" value={automation.warm_followups_sent} />
            <MiniStat label="In 24h window" value={automation.waiting_24h_window} />
            <MiniStat label="Due next run" value={automation.pending_due_now} highlight />
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-[#F3C9D3]/40 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F6EFE9] text-left">
            <tr>
              {["Email", "Source", "Downloads", "Last download", "Follow-up", "Subscribed", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-[0.15em] font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5C5C5C]">No subscribers yet.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-[#F3C9D3]/30">
                <td className="px-4 py-3">{s.email}</td>
                <td className="px-4 py-3 text-xs text-[#5C5C5C]">{s.source}</td>
                <td className="px-4 py-3">
                  {s.download_count > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[#E8A6B8] text-white">
                      {s.download_count}× downloaded
                    </span>
                  ) : (
                    <span className="text-xs text-[#5C5C5C]/60">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#5C5C5C]">
                  {s.last_downloaded_at ? new Date(s.last_downloaded_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-xs">
                  {s.warm_followup_sent_at ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F3C9D3]/40 text-[#3A3A3A] text-[10px] uppercase tracking-wider"
                      title={new Date(s.warm_followup_sent_at).toLocaleString()}
                    >
                      Sent
                    </span>
                  ) : (
                    <span className="text-[#5C5C5C]/60">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#5C5C5C]">{new Date(s.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => sendWarmFollowup(s)}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F6EFE9] hover:bg-[#F3C9D3]/60 mr-1"
                    data-testid={`send-warm-${s.id}`}
                    title="Send warm follow-up now"
                  >
                    <Mail size={12} /> {s.warm_followup_sent_at ? "Re-send" : "Send"}
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-red-700 bg-red-50 hover:bg-red-100"
                    data-testid={`admin-delete-sub-${s.id}`}
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#F3C9D3]/40 px-5 py-4">
      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5C5C5C]">{label}</p>
      <p className="font-serif-display text-3xl text-[#3A3A3A] mt-1">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, highlight = false }) {
  return (
    <div
      className={`rounded-xl px-4 py-3 ${
        highlight ? "bg-[#E8A6B8]/15 border border-[#E8A6B8]/40" : "bg-[#F6EFE9] border border-[#F3C9D3]/40"
      }`}
    >
      <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C5C5C]">{label}</p>
      <p className={`font-serif-display text-xl mt-1 ${highlight ? "text-[#E8A6B8]" : "text-[#3A3A3A]"}`}>{value}</p>
    </div>
  );
}

function BookingsTab({ token }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    adminApi(token).get("/admin/bookings").then((r) => setItems(r.data));
  }, [token]);
  useEffect(load, [load]);
  return (
    <div data-testid="admin-bookings">
      <ActionRow onRefresh={load} count={items.length} label="bookings" />
      <div className="overflow-x-auto rounded-2xl border border-[#F3C9D3]/40 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F6EFE9] text-[#3A3A3A] text-left">
            <tr>
              {["Name", "Email", "Phone", "Option", "Goal", "Experience", "Date"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-[0.15em] font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5C5C5C]">No bookings yet.</td></tr>
            )}
            {items.map((b) => (
              <tr key={b.id} className="border-t border-[#F3C9D3]/30">
                <td className="px-4 py-3">{b.name}</td>
                <td className="px-4 py-3">{b.email}</td>
                <td className="px-4 py-3">{b.phone}</td>
                <td className="px-4 py-3">{b.coaching_option}</td>
                <td className="px-4 py-3 max-w-xs truncate">{b.main_goal}</td>
                <td className="px-4 py-3">{b.training_experience}</td>
                <td className="px-4 py-3 text-xs text-[#5C5C5C]">{new Date(b.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersTab({ token }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    adminApi(token).get("/admin/orders").then((r) => setItems(r.data.orders || []));
  }, [token]);
  useEffect(load, [load]);
  return (
    <div data-testid="admin-orders">
      <ActionRow onRefresh={load} count={items.length} label="orders" />
      <div className="overflow-x-auto rounded-2xl border border-[#F3C9D3]/40 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F6EFE9] text-left">
            <tr>
              {["Program", "Amount", "Status", "Payment", "Session", "Date"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-[0.15em] font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C5C5C]">No orders yet.</td></tr>
            )}
            {items.map((o) => (
              <tr key={o.session_id} className="border-t border-[#F3C9D3]/30">
                <td className="px-4 py-3">{o.program_title}</td>
                <td className="px-4 py-3">{o.currency?.toUpperCase()} {Number(o.amount).toFixed(2)}</td>
                <td className="px-4 py-3">{o.status}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-1 rounded-full text-[10px] uppercase tracking-wider ${
                    o.payment_status === "paid" ? "bg-[#E8A6B8] text-white" : "bg-[#F3C9D3]/40 text-[#3A3A3A]"
                  }`}>{o.payment_status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#5C5C5C] max-w-[200px] truncate">{o.session_id}</td>
                <td className="px-4 py-3 text-xs text-[#5C5C5C]">{new Date(o.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY_POST = {
  title: "",
  excerpt: "",
  content: "",
  category: "Strength Training",
  image_url: "",
  author: "Maya",
};

function BlogTab({ token }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null or post object (or 'new')

  const load = useCallback(() => {
    adminApi(token).get("/admin/blog/posts").then((r) => setItems(r.data));
  }, [token]);
  useEffect(load, [load]);

  const save = async (form) => {
    try {
      if (editing && editing.id) {
        await adminApi(token).put(`/admin/blog/posts/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await adminApi(token).post("/admin/blog/posts", form);
        toast.success("Post created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await adminApi(token).delete(`/admin/blog/posts/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const broadcast = async (post) => {
    if (!window.confirm(`Send "${post.title}" to all subscribers?`)) return;
    try {
      const { data } = await adminApi(token).post(`/admin/blog/posts/${post.id}/send`);
      if (data.reason === "resend_not_configured") {
        toast.error("Resend not configured — add RESEND_API_KEY first");
        return;
      }
      toast.success(`Sent to ${data.sent}/${data.total} subscribers`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    }
  };

  return (
    <div data-testid="admin-blog">
      <ActionRow
        onRefresh={load}
        count={items.length}
        label="posts"
        action={
          <button
            onClick={() => setEditing({ ...EMPTY_POST })}
            className="btn-pink text-xs"
            data-testid="admin-new-post"
          >
            <Plus size={14} /> New Post
          </button>
        }
      />

      {editing && (
        <PostEditor
          initial={editing}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#F3C9D3]/40 overflow-hidden" data-testid={`admin-post-${p.id}`}>
            {p.image_url && (
              <div className="aspect-[16/10] overflow-hidden">
                <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5">
              <p className="eyebrow text-[#E8A6B8] mb-2 text-[10px]">{p.category}</p>
              <h3 className="font-serif-display text-xl text-[#3A3A3A]">{p.title}</h3>
              <p className="text-xs text-[#5C5C5C] line-clamp-2 mt-2">{p.excerpt}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setEditing(p)}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F6EFE9] hover:bg-[#F3C9D3]"
                  data-testid={`admin-edit-${p.id}`}
                >
                  <Edit3 size={12} /> Edit
                </button>
                <button
                  onClick={() => broadcast(p)}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#E8A6B8] text-white hover:bg-[#D18A9E]"
                  data-testid={`admin-send-${p.id}`}
                >
                  <Send size={12} /> Send
                </button>
                <button
                  onClick={() => remove(p.id)}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-red-700 bg-red-50 hover:bg-red-100"
                  data-testid={`admin-delete-${p.id}`}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PostEditor({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[#F3C9D3]/40 p-6 mb-6 space-y-4" data-testid="admin-post-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-serif-display text-2xl text-[#3A3A3A]">{form.id ? "Edit Post" : "New Post"}</h3>
        <button type="button" onClick={onCancel} className="text-[#5C5C5C] hover:text-[#3A3A3A]" data-testid="admin-cancel-editor"><X size={20} /></button>
      </div>
      <input className="hfh-admin-input" placeholder="Title" required value={form.title} onChange={update("title")} data-testid="post-title" />
      <input className="hfh-admin-input" placeholder="Category (e.g. Strength Training)" required value={form.category} onChange={update("category")} data-testid="post-category" />
      <input className="hfh-admin-input" placeholder="Image URL" required value={form.image_url} onChange={update("image_url")} data-testid="post-image" />
      <input className="hfh-admin-input" placeholder="Excerpt (1-2 lines)" required value={form.excerpt} onChange={update("excerpt")} data-testid="post-excerpt" />
      <textarea className="hfh-admin-input" placeholder="Content" rows={8} required value={form.content} onChange={update("content")} data-testid="post-content" />
      <input className="hfh-admin-input" placeholder="Author" value={form.author} onChange={update("author")} data-testid="post-author" />
      <div className="flex gap-3">
        <button type="submit" className="btn-pink text-xs" data-testid="post-save"><Save size={14}/> Save</button>
        <button type="button" onClick={onCancel} className="btn-outline text-xs">Cancel</button>
      </div>
      <style>{`.hfh-admin-input{width:100%;background:#fff;border:1px solid #F3C9D3;border-radius:0.85rem;padding:0.7rem 1rem;font-size:0.9rem;color:#3A3A3A;font-family:inherit}
      .hfh-admin-input:focus{outline:none;border-color:#E8A6B8;box-shadow:0 0 0 4px rgba(232,166,184,0.15)}`}</style>
    </form>
  );
}

function ProgramsTab({ token }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    adminApi(token).get("/admin/programs").then((r) => setItems(r.data));
  }, [token]);
  useEffect(load, [load]);

  const save = async (id, patch) => {
    try {
      await adminApi(token).put(`/admin/programs/${id}`, patch);
      toast.success("Program updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div data-testid="admin-programs">
      <ActionRow onRefresh={load} count={items.length} label="programs" />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => (
          <ProgramEditor key={p.id} program={p} onSave={save} />
        ))}
      </div>
    </div>
  );
}

function ProgramEditor({ program, onSave }) {
  const [form, setForm] = useState({
    title: program.title,
    tagline: program.tagline,
    description: program.description,
    price: program.price,
    image_url: program.image_url,
    active: program.active,
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onSave(program.id, { ...form, price: parseFloat(form.price) });
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[#F3C9D3]/40 p-5 space-y-3" data-testid={`admin-program-${program.id}`}>
      <p className="eyebrow text-[#E8A6B8] text-[10px]">{program.id}</p>
      <input className="hfh-admin-input" value={form.title} onChange={update("title")} data-testid={`program-title-${program.id}`}/>
      <input className="hfh-admin-input" value={form.tagline} onChange={update("tagline")}/>
      <textarea className="hfh-admin-input" rows={3} value={form.description} onChange={update("description")}/>
      <input className="hfh-admin-input" type="number" step="0.01" value={form.price} onChange={update("price")} data-testid={`program-price-${program.id}`}/>
      <input className="hfh-admin-input" value={form.image_url} onChange={update("image_url")} placeholder="Image URL"/>
      <button type="submit" className="btn-pink w-full text-xs" data-testid={`save-program-${program.id}`}><Save size={14}/> Save</button>
      <style>{`.hfh-admin-input{width:100%;background:#fff;border:1px solid #F3C9D3;border-radius:0.85rem;padding:0.55rem 0.85rem;font-size:0.85rem;color:#3A3A3A;font-family:inherit}
      .hfh-admin-input:focus{outline:none;border-color:#E8A6B8;box-shadow:0 0 0 3px rgba(232,166,184,0.15)}`}</style>
    </form>
  );
}

function ActionRow({ onRefresh, count, label, action }) {
  return (
    <div className="flex justify-between items-center mb-5">
      <p className="text-sm text-[#5C5C5C]">{count} {label}</p>
      <div className="flex gap-2 items-center">
        <button onClick={onRefresh} className="text-xs inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#F6EFE9] hover:bg-[#F3C9D3]/60" data-testid="admin-refresh">
          <RefreshCcw size={12}/> Refresh
        </button>
        {action}
      </div>
    </div>
  );
}
